import type { AdminAgentReasoningEffort, AdminAgentSettingsControlsProps } from "../models/admin-agent.models";
import { formatAdminAgentCost, formatAdminAgentReasoningEffort } from "../utils/admin-agent.utils";
import type { ChangeEvent } from "react";

// Keep the selected agent and observed usage on the backend conversation for each permitted reader.
// Settings apply to the next send or resume; stopping remains available throughout longer runs.
const AdminAgentSettingsControls = ({ conversation, busy, onSettingsChange }: AdminAgentSettingsControlsProps) => {
  const { settings, availableModels, usage, run } = conversation;
  const selectedModel = availableModels.find((model) => model.modelName === settings.modelName);
  const disabled = busy || conversation.status === "running" || Boolean(conversation.pendingApproval);
  const usageDetails = `${usage.inputTokens.toLocaleString()} input · ${usage.outputTokens.toLocaleString()} output · ${usage.modelCalls.toLocaleString()} model calls. Counts cover responses received by OpsLink; interrupted calls may be missing.`;

  const changeModel = (event: ChangeEvent<HTMLSelectElement>) => {
    const model = availableModels.find((option) => option.modelName === event.target.value);

    if (model && model.reasoningEfforts.length) {
      const reasoningEffort = model.reasoningEfforts.includes(settings.reasoningEffort)
        ? settings.reasoningEffort
        : model.reasoningEfforts.includes("high")
          ? "high"
          : model.reasoningEfforts[0]!;
      void onSettingsChange({ modelName: model.modelName, reasoningEffort });
    }
  };

  const changeReasoning = (event: ChangeEvent<HTMLSelectElement>) => {
    void onSettingsChange({ ...settings, reasoningEffort: event.target.value as AdminAgentReasoningEffort });
  };

  return (
    <div
      data-test="admin-agent-settings"
      className="shrink-0 border-b border-gray-200 px-3 py-2 text-xs dark:border-gray-700"
    >
      <div className="flex flex-wrap gap-2">
        <label className="flex min-w-0 flex-1 items-center gap-2" htmlFor="admin-agent-model">
          Agent
          <select
            id="admin-agent-model"
            data-test="admin-agent-model-select"
            value={settings.modelName}
            disabled={disabled || availableModels.length === 1}
            onChange={changeModel}
            className="h-8 min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800"
          >
            {availableModels.map((model) => (
              <option key={model.modelName} value={model.modelName}>
                {model.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2" htmlFor="admin-agent-reasoning">
          Reasoning
          <select
            id="admin-agent-reasoning"
            data-test="admin-agent-reasoning-select"
            value={settings.reasoningEffort}
            disabled={disabled || selectedModel?.reasoningEfforts.length === 1}
            title={
              settings.reasoningEffort === "default"
                ? "Compatibility mode leaves reasoning to the provider. Sol 6 normally defaults to Medium."
                : undefined
            }
            onChange={changeReasoning}
            className="h-8 rounded border border-gray-300 bg-white px-2 capitalize disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800"
          >
            {selectedModel?.reasoningEfforts.map((effort) => (
              <option key={effort} value={effort}>
                {formatAdminAgentReasoningEffort(effort)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div
        data-test="admin-agent-conversation-usage"
        className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-gray-500 dark:text-gray-400"
      >
        <span title={usageDetails}>{usage.totalTokens.toLocaleString()} tokens this chat</span>
        <span
          data-test="admin-agent-estimated-cost"
          title="Estimated USD at standard text rates, without cache discounts or gateway adjustments. Includes only responses received by OpsLink."
        >
          {formatAdminAgentCost(usage)}
        </span>
        {run && (
          <span data-test="admin-agent-step-count">
            {run.stepCount}/{run.maxSteps} steps this run
          </span>
        )}
      </div>
    </div>
  );
};

export default AdminAgentSettingsControls;
